import { useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useBuckets } from "./shared/useBuckets";
import { SettlementDrawer } from "./shared/SettlementDrawer";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { StatusBadge } from "../applications/shared/StatusBadge";
import { formatApplicantName } from "../applications/shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { fmtCLP } from "../dashboard/format";
import {
  SETTLEMENT_STATUS_LABELS,
  formatDateShort,
  daysFrom,
  settlementStatusTone,
} from "./shared/labels";
import type { PendingReviewRow } from "./types";

interface Props {
  /**
   * "all" = todas las en revisión.
   * "active" = solo las con recordatorio ya enviado (reminderSentAt != null) →
   *           ventana activa, conviene revisar primero.
   */
  variant?: "all" | "active";
  title: string;
  subtitle: string;
  eyebrow: string;
}

export function PendingScreen({ variant = "all", title, subtitle, eyebrow }: Props) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const query = useBuckets();

  const rows = useMemo(() => {
    const all = query.data?.buckets.pendientesRevision ?? [];
    if (variant === "active") return all.filter((r) => r.reminderSentAt !== null);
    return all;
  }, [query.data, variant]);

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">{eyebrow}</p>
          <h1 className="nx-h1">{title}</h1>
          <p className="text-sm text-fg-muted mt-1">{subtitle}</p>
        </div>
        <button
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="nx-btn-secondary"
        >
          <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      {query.isLoading ? (
        <LoadingBlock label="Cargando liquidaciones…" />
      ) : query.isError ? (
        <ErrorBlock onRetry={() => query.refetch()} />
      ) : (
        <ApplicationsTable<PendingReviewRow & { id: number }>
          rows={rows.map((r) => ({ ...r, id: r.settlementId }))}
          columns={columns}
          page={1}
          pageSize={rows.length || 1}
          total={rows.length}
          onPageChange={() => {}}
          onRowClick={(r) => setSelectedId(r.settlementId)}
          selectedId={selectedId}
          emptyLabel={
            variant === "active"
              ? "No hay liquidaciones con recordatorio enviado."
              : "No hay liquidaciones pendientes de revisión."
          }
        />
      )}

      <SettlementDrawer
        settlementId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

const columns: Column<PendingReviewRow & { id: number }>[] = [
  {
    key: "settlementId",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.settlementId}</span>,
    width: "min",
  },
  {
    key: "event",
    header: "Evento",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg truncate max-w-xs">{r.eventName}</div>
        <div className="text-2xs text-fg-subtle">{formatDateShort(r.eventDate)}</div>
      </div>
    ),
  },
  {
    key: "productor",
    header: "Productor",
    cell: (r) => (
      <div>
        <div className="text-xs text-fg">
          {formatApplicantName({ firstName: r.productorNombre })}
        </div>
        <div className="text-2xs text-fg-subtle break-all">{r.productorEmail ?? "—"}</div>
      </div>
    ),
  },
  {
    key: "amount",
    header: "A transferir",
    cell: (r) => <span className="text-fg font-semibold tabular-nums">{fmtCLP(r.amountToTransfer)}</span>,
    align: "right",
    width: "min",
  },
  {
    key: "deadline",
    header: "Deadline",
    cell: (r) => <DeadlineCell deadlineAt={r.revisionDeadlineAt} />,
    width: "min",
  },
  {
    key: "reminder",
    header: "Recordatorio",
    cell: (r) =>
      r.reminderSentAt ? (
        <span className="text-2xs text-info">✓ enviado</span>
      ) : (
        <span className="text-2xs text-fg-subtle">—</span>
      ),
    width: "min",
  },
  {
    key: "status",
    header: "Estado",
    cell: (r) => (
      <StatusBadge
        status={r.status}
        tone={settlementStatusTone(r.status)}
        label={SETTLEMENT_STATUS_LABELS[r.status]}
      />
    ),
    width: "min",
  },
];

function DeadlineCell({ deadlineAt }: { deadlineAt: string | null }) {
  if (!deadlineAt) return <span className="text-fg-subtle text-xs">—</span>;
  const days = daysFrom(deadlineAt);
  if (days === null) return <span className="text-fg-subtle text-xs">—</span>;

  const text = days < 0 ? `Venció hace ${Math.abs(days)}d` : days === 0 ? "Vence hoy" : `Faltan ${days}d`;
  const color = days < 0 ? "text-danger" : days <= 1 ? "text-warning" : "text-fg-muted";

  return (
    <div>
      <div className={`text-xs ${color} tabular-nums`}>{text}</div>
      <div className="text-2xs text-fg-subtle">{formatDateShort(deadlineAt)}</div>
    </div>
  );
}
